import { http } from '../store/axiosBaseQuery.js'

/**
 * Three steps, and the bytes never pass through the API.
 *
 * Every upload in the product works the same way: ask for permission, PUT the
 * bytes straight at the store, then tell the API they landed. The headers that
 * come back are **not advice** — they are signed into the URL, and the store
 * refuses a request that does not carry them exactly. So the size stated on the
 * screen is the size that can be uploaded, rather than a number the client is
 * trusted with.
 */
export interface UploadTicket {
  asset_id: string
  upload_url: string
  upload_headers: Record<string, string>
  expires_in_seconds: number
}

export interface UploadResult {
  assetId: string
}

/**
 * Send the bytes to the address the API gave out.
 *
 * Deliberately `fetch` rather than the shared axios client: this request goes to
 * Cloudflare, not to us, and it must not carry our Authorization header or our
 * cookies. A signed URL that also arrives with a session token is a session token
 * in somebody else's logs.
 */
export async function putBytes(
  ticket: UploadTicket,
  file: File,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response
  try {
    response = await fetch(ticket.upload_url, {
      method: 'PUT',
      headers: ticket.upload_headers,
      body: file,
      signal,
    })
  } catch (error) {
    /**
     * The bytes never left the machine.
     *
     * `fetch` throws a bare `TypeError: Failed to fetch` for everything that
     * happens before a response — no network, and, far more often here, a
     * refused CORS preflight. The client met exactly that: *"add class
     * uploaded an image gave failed to fetch error"*, and the cause was the
     * storage buckets having no CORS rules at all, so the browser refused to
     * send. The message named none of that.
     *
     * An abort is the one case that is not a fault — somebody closed the
     * panel — so it is rethrown untouched for the caller to ignore.
     */
    if (signal?.aborted || (error as Error)?.name === 'AbortError') throw error
    throw new Error(
      'The file could not be sent to storage. The upload never started, which usually means the storage bucket is not set up to accept uploads from this address.',
    )
  }
  if (!response.ok) {
    throw new Error(
      response.status === 403
        ? 'The file did not match what was asked for. Choose it again and retry.'
        : 'The file could not be uploaded. Check your connection and try again.',
    )
  }
}

/**
 * Claim a slot from an admin upload route, send the bytes, and hand back the id.
 *
 * `extra` is not decoration. The two upload routes on this surface do **not** take
 * the same body: a class resource asks for the type and the size and nothing else,
 * while an event cover also requires `kind: 'event_cover'` and accepts the alt text.
 * This helper sent neither, so every cover image would have been refused with a
 * validation error at the moment somebody chose one — the component existed, the
 * route existed, and nothing had ever put the two together.
 */
export async function uploadTo(
  url: string,
  file: File,
  extra: Record<string, unknown> = {},
  signal?: AbortSignal,
): Promise<UploadResult> {
  const { data } = await http.post<UploadTicket>(
    url,
    { mime_type: file.type, size_bytes: file.size, ...extra },
    { headers: { 'Idempotency-Key': crypto.randomUUID() }, signal },
  )
  await putBytes(data, file, signal)
  return { assetId: data.asset_id }
}

/** "2.4 MB" — the size as somebody reads it, for a limit stated before the picker opens. */
export function readableSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
